import { useAccount } from "wagmi";

import type { VenueId } from "@/config/contracts";
import { usePositionSummary } from "@/hooks/use-position-summary";

import { PositionCard } from "./position-card";

type Props = {
  readonly onModify: (venue: VenueId) => void;
  readonly onClose: (venue: VenueId) => void;
  readonly onNew: () => void;
};

const Shell = ({ children }: { children: React.ReactNode }) => (
  <section
    className="surface-strong rounded-[var(--radius-lg)] px-6 md:px-9 py-12 text-center fade-up"
    aria-label="Positions"
  >
    {children}
  </section>
);

const Eyebrow = ({ text }: { text: string }) => (
  <div
    className="font-mono text-[10px] tracking-[0.22em] uppercase mb-4"
    style={{ color: "var(--ink-4)" }}
  >
    {text}
  </div>
);

export const PositionsPanel = ({ onModify, onClose, onNew }: Props) => {
  const { isConnected } = useAccount();
  const morpho = usePositionSummary("morpho");
  const ajna = usePositionSummary("ajna");
  const summaries = [morpho, ajna];

  if (!isConnected) {
    return (
      <Shell>
        <Eyebrow text="Your positions" />
        <p
          className="font-serif text-[24px]"
          style={{ fontWeight: 300, color: "var(--ink-2)" }}
        >
          Connect a wallet to see your open positions.
        </p>
      </Shell>
    );
  }

  const open = summaries.filter((s) => s.hasPosition);
  const stillLoading = summaries.some((s) => s.loading);

  if (open.length === 0 && stillLoading) {
    return (
      <Shell>
        <Eyebrow text="Your positions" />
        <p
          className="font-mono text-[12px] tracking-[0.12em] uppercase"
          style={{ color: "var(--ink-3)" }}
        >
          Loading…
        </p>
      </Shell>
    );
  }

  if (open.length === 0) {
    return (
      <Shell>
        <Eyebrow text="Your positions" />
        <p
          className="font-serif text-[26px] md:text-[30px]"
          style={{ fontWeight: 300, color: "var(--ink-2)" }}
        >
          No open position.
        </p>
        <button
          type="button"
          onClick={onNew}
          className="mt-6 font-mono text-[12px] tracking-[0.18em] uppercase transition-opacity hover:opacity-70"
          style={{ color: "var(--ink-1)", fontWeight: 500 }}
        >
          Open one →
        </button>
      </Shell>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
      {open.map((s) => (
        <PositionCard
          key={s.venue}
          summary={s}
          onModify={() => onModify(s.venue)}
          onClose={() => onClose(s.venue)}
        />
      ))}
    </div>
  );
};
